<template>
    <ul class="divide-y divide-gray-100 border rounded mb-2 max-h-64 overflow-y-auto">
        <template v-for="group in groups" :key="group.key">
            <!-- Gebäude-Kopfzeile mit Sammelhaken. Sie erscheint NUR, wenn das
                 Projekt überhaupt Gebäude hat — ein Projekt ohne Gruppierung
                 sieht aus wie vorher (Thema 18). -->
            <li
                v-if="grouped"
                class="flex items-center px-3 py-2 bg-minplan-lightwhite"
            >
                <input
                    type="checkbox"
                    class="rounded mr-3"
                    :checked="isGroupSelected(group)"
                    :indeterminate.prop="isGroupPartial(group)"
                    @change="toggleGroup(group, $event.target.checked)"
                />
                <span class="text-sm font-semibold text-gray-900">{{ group.name }}</span>
                <span class="ml-auto text-xs text-gray-400">
                    {{ group.apartments.length }}
                </span>
            </li>
            <li
                v-for="apartment in group.apartments"
                :key="apartment.uuid"
                class="flex items-center px-3 py-2"
                :class="{ 'pl-8': grouped }"
            >
                <input
                    type="checkbox"
                    class="rounded mr-3"
                    :value="apartment.uuid"
                    :checked="modelValue.includes(apartment.uuid)"
                    @change="toggle(apartment.uuid, $event.target.checked)"
                />
                <span class="text-sm text-gray-900">{{ apartment.name }}</span>
            </li>
        </template>
    </ul>
</template>

<script>
import { defineComponent } from "vue";

// Thema 18 (`product/UEBERGABE-THEMA-18-OFFEN.md`): gemeinsame Auswahlliste
// für das Archiv-Modal und das Verteil-Modal. Sie zeigt die Wohnungen in der
// Reihenfolge der Gebäude-Gruppierung, mit einer Kopfzeile je Gebäude und
// einem Sammelhaken «ganzes Gebäude».
//
// Wichtig für die Rückwärtsverträglichkeit: Ohne Gebäude-Angabe (Projekt ohne
// Gruppierung) rendert die Komponente exakt dieselbe flache Liste wie zuvor —
// keine Kopfzeilen, keine zusätzlichen Checkboxen. Die bestehenden Vitest-Specs
// beider Modale zählen Checkboxen nach Position; diese Regel hält sie gültig.
//
// Das Backend bleibt UUID-basiert: die Komponente löst «ganzes Gebäude» in die
// einzelnen Wohnungs-UUIDs auf, es gibt keine neue Schnittstelle.
export default defineComponent({
    props: {
        apartments: { type: Array, required: true },
        modelValue: { type: Array, required: true },
    },
    emits: ["update:modelValue"],
    computed: {
        grouped() {
            return this.apartments.some((apartment) => !!apartment.building);
        },
        // Aufeinanderfolgende Wohnungen mit demselben Gebäude-Pfad bilden eine
        // Gruppe. Weil die Liste bereits in Anzeige-Reihenfolge kommt
        // (ApartmentOrdering), genügt das — es braucht keinen Baum im Frontend.
        groups() {
            const groups = [];

            this.apartments.forEach((apartment) => {
                const name = apartment.building || "";
                const last = groups[groups.length - 1];

                if (last && last.name === name) {
                    last.apartments.push(apartment);
                    return;
                }

                groups.push({ key: `${name}-${groups.length}`, name, apartments: [apartment] });
            });

            return groups;
        },
    },
    methods: {
        toggle(uuid, checked) {
            const next = checked
                ? [...new Set([...this.modelValue, uuid])]
                : this.modelValue.filter((selected) => selected !== uuid);

            this.$emit("update:modelValue", next);
        },
        groupUuids(group) {
            return group.apartments.map((apartment) => apartment.uuid);
        },
        isGroupSelected(group) {
            return this.groupUuids(group).every((uuid) => this.modelValue.includes(uuid));
        },
        isGroupPartial(group) {
            const uuids = this.groupUuids(group);
            const selected = uuids.filter((uuid) => this.modelValue.includes(uuid));

            return selected.length > 0 && selected.length < uuids.length;
        },
        toggleGroup(group, checked) {
            const uuids = this.groupUuids(group);

            this.$emit(
                "update:modelValue",
                checked
                    ? [...new Set([...this.modelValue, ...uuids])]
                    : this.modelValue.filter((uuid) => !uuids.includes(uuid)),
            );
        },
    },
});
</script>
